import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Chip, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import TicketForm from './TicketForm';
import { createApiUrl } from '../../config/api';

const TicketList = ({ onNavigateToTicket }) => {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTickets, setTotalTickets] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category_id: '',
    customer_id: ''
  });
  
  // Metadata
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });
      
      const response = await fetch(createApiUrl(`api/tickets?${queryParams}`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        setTickets(data.tickets);
        setTotalTickets(data.pagination.total);
      } else {
        setError(data.error || 'Failed to fetch tickets');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  // Fetch metadata
  const fetchMetadata = async () => {
    try {
      const metadataRes = await fetch(createApiUrl('api/tickets/metadata'), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const metadataData = await metadataRes.json();
      
      if (metadataRes.ok) {
        setCategories(metadataData.categories || []);
        setCustomers(metadataData.customers || []);
      }
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetchMetadata();
  }, []);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle ticket actions
  const handleCreateTicket = () => {
    setSelectedTicket(null);
    setCreateDialogOpen(true);
  };

  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setEditDialogOpen(true);
  };

  const handleViewTicket = (ticket) => {
    if (onNavigateToTicket) {
      onNavigateToTicket(ticket.id);
    }
  };

  const handleTicketCreated = (newTicket) => {
    setSuccess('Ticket created successfully');
    setCreateDialogOpen(false);
    fetchTickets();
  };

  const handleTicketUpdated = (updatedTicket) => {
    setSuccess('Ticket updated successfully');
    setEditDialogOpen(false);
    fetchTickets();
  };

  // Status chip color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'warning'; // Open
      case 2: return 'success'; // Resolved
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return t('tasks.open');
      case 2: return t('tickets.resolved');
      default: return t('crm.unknown');
    }
  };

  // Priority color mapping
  const getPriorityColor = (priorityName) => {
    switch (priorityName?.toLowerCase()) {
      case 'low': return 'success';
      case 'normal': return 'info';
      case 'high': return 'warning';
      case 'critical': return 'error';
      case 'emergency': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-demo-target="page-tickets-root">
      <Card>
        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ 
                fontWeight: 'bold',
                color: '#000062',
                fontSize: { xs: '1.5rem', md: '2.125rem' },
                mb: 3
              }}
            >
              Support Tickets
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Tooltip title={t('crm.refresh')}>
                <span>
                  <IconButton 
                    onClick={fetchTickets} 
                    disabled={loading}
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTicket}
                sx={{ 
                  minWidth: { xs: '100%', sm: 'auto' },
                  flex: { xs: 1, sm: 'none' }
                }}
                size="small"
              >
                Create Ticket
              </Button>
            </Box>
          </Box>

          {/* Public Support Portal Link */}
          <Alert 
            severity="info" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<OpenInNewIcon />}
                href="/support"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ flexShrink: 0 }}
              >
                Open Portal
              </Button>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2">
                <strong>Customer-Facing Support Portal:</strong> Share <a href="/support" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>this link</a> with customers to submit tickets or check ticket status.
              </Typography>
            </Box>
          </Alert>

          {/* Filters */}
          <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Search tickets..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid size={{ xs: 6, sm: 6, md: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label={t('tickets.status')}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="1">Open</MenuItem>
                  <MenuItem value="2">Resolved</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category_id}
                  label={t('tickets.category')}
                  onChange={(e) => handleFilterChange('category_id', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={filters.customer_id}
                  label={t('tickets.customer')}
                  onChange={(e) => handleFilterChange('customer_id', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.first_name} {customer.last_name} {customer.company_name && `(${customer.company_name})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Tickets Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Ticket Name</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Assigned To</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow key={ticket.id} hover>
                        <TableCell>{ticket.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {ticket.name}
                          </Typography>
                          {ticket.notes_count > 0 && (
                            <Chip 
                              label={`${ticket.notes_count} notes`} 
                              size="small" 
                              variant="outlined" 
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {ticket.customer_name}
                          </Typography>
                          {ticket.company_name && (
                            <Typography variant="caption" color="text.secondary">
                              {ticket.company_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{ticket.category_name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusText(ticket.status)} 
                            color={getStatusColor(ticket.status)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          {ticket.priority_name && (
                            <Chip 
                              label={ticket.priority_name} 
                              color={getPriorityColor(ticket.priority_name)} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(ticket.created_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {ticket.assigned_to_name || t('tasks.unassigned')}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={t('tasks.viewDetails')}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewTicket(ticket)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('tickets.editTicket')}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditTicket(ticket)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {tickets.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography>No tickets found</Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1 }}>
                    {tickets.map((ticket) => (
                      <Card 
                        key={ticket.id} 
                        sx={{ 
                          mb: 1, 
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 3
                          }
                        }}
                        onClick={() => handleViewTicket(ticket)}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box flex={1}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                                #{ticket.id}: {ticket.name}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                {ticket.customer_name}
                                {ticket.company_name && (
                                  <span> • {ticket.company_name}</span>
                                )}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={getStatusText(ticket.status)}
                                color={getStatusColor(ticket.status)}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditTicket(ticket);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                            <Box>
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                Category: {ticket.category_name}
                              </Typography>
                              {ticket.priority_name && (
                                <Chip
                                  label={ticket.priority_name}
                                  color={getPriorityColor(ticket.priority_name)}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.6rem', height: '18px', mt: 0.5 }}
                                />
                              )}
                            </Box>
                            <Box>
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {t('tickets.assigned')}: {ticket.assigned_to_name || t('tasks.unassigned')}
                              </Typography>
                              {ticket.notes_count > 0 && (
                                <Chip 
                                  label={`${ticket.notes_count} notes`} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ fontSize: '0.6rem', height: '18px', mt: 0.5 }}
                                />
                              )}
                            </Box>
                          </Box>
                          
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                            Created: {new Date(ticket.created_date).toLocaleDateString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>

              <TablePagination
                component="div"
                count={totalTickets}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ mt: { xs: 1, md: 0 } }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            height: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Create New Ticket</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          <TicketForm 
            onSubmit={handleTicketCreated}
            onCancel={() => setCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Ticket Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            height: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Edit Ticket</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          <TicketForm 
            ticket={selectedTicket}
            onSubmit={handleTicketUpdated}
            onCancel={() => setEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>



      {/* Snackbars */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TicketList; 